import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  Copy,
  Check,
  HandCoins,
  MessagesSquare,
  Search,
  // Phase 36 Batch AA.4 — MAO baseline icon
  Calculator,
  // Phase 36 — seller-side icons
  Tag,
  ShieldCheck,
  Megaphone,
  Mail,
  // Phase 36 Batch AB — document-signals icons
  FileText,
  ArrowRight,
  AlertTriangle,
  X,
} from "lucide-react";
import { useFmt } from "@/lib/useFmt";

const TIER_META = {
  conservative_offer: {
    label: "Conservative",
    sub: "Lowest credible bid",
    cls: "border-slate-200 bg-white",
  },
  recommended_offer: {
    label: "Recommended",
    sub: "Our pick",
    cls: "border-indigo-300 bg-indigo-50/50 ring-2 ring-indigo-100 scale-[1.02]",
    highlight: true,
  },
  max_offer: {
    label: "Max",
    sub: "Walk-away ceiling",
    cls: "border-slate-200 bg-white",
  },
};

function OfferCard({ tierKey, tier, asking }) {
  const meta = TIER_META[tierKey];
  const fmt = useFmt();
  return (
    <div
      data-testid={`offer-${tierKey}`}
      className={`rounded-2xl border p-6 shadow-sm transition-all ${meta.cls}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{meta.label}</div>
          <div className="text-xs text-slate-500">{meta.sub}</div>
        </div>
        {meta.highlight && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            Recommended
          </span>
        )}
      </div>
      <div className="mt-4 text-3xl font-bold tracking-tight text-slate-900 tabular-nums">
        {fmt.money(tier.price)}
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {tier.delta_pct_from_ask >= 0 ? "+" : ""}{tier.delta_pct_from_ask.toFixed(1)}% vs ask
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-700">{tier.justification}</p>
    </div>
  );
}

export default function NegotiationTab({ deal, onRun, running, onSwitchTab }) {
  const neg = deal?.negotiation;
  const fmt = useFmt();
  const [copied, setCopied] = useState(false);

  // Phase 36 — role-aware branching. The seller-side payload has a
  // completely different shape (no `offers`, has `minimum_acceptable_price`,
  // `listing_tweak_suggestions`, etc.) so we route to a dedicated
  // renderer when role=seller.
  const role = (neg?.role || deal?.role || "buyer").toLowerCase();
  if (role === "seller" && neg) {
    return (
      <SellerNegotiationView
        neg={neg}
        deal={deal}
        onRun={onRun}
        running={running}
      />
    );
  }

  if (running && !neg) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-medium text-indigo-600">
          <Loader2 className="h-4 w-4 animate-spin" /> AI is drafting offer language…
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-xl bg-slate-200" />
          ))}
        </div>
      </div>
    );
  }

  if (!neg) {
    // Phase 36 — empty-state copy switches to seller framing when the
    // deal is being analysed from the owner's perspective.
    const sellerEmpty = role === "seller";
    return (
      <div className="space-y-6">
        {/* Phase 36 Batch AA.4 — MAO card lives ABOVE the empty-state
            CTA so flippers can see their 70% baseline even before they
            run the AI negotiation engine. */}
        <MAOTargetCard deal={deal} neg={neg} />
        {/* Phase 36 Batch AB — even before negotiation has been run,
            surface the docs nudge so users can think about uploading
            inspection / HOA / title BEFORE asking the AI. */}
        <DocumentSignalsSection
          neg={null}
          deal={deal}
          onSwitchTab={onSwitchTab}
        />
        <div
          data-testid="neg-empty"
          className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center"
        >
          <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl ${
            sellerEmpty ? "bg-amber-50 text-amber-600" : "bg-indigo-50 text-indigo-600"
          }`}>
            {sellerEmpty ? <Tag className="h-6 w-6" /> : <HandCoins className="h-6 w-6" />}
          </div>
          <h3 className="text-xl font-semibold text-slate-900">
            {sellerEmpty ? "Listing Strategy Engine" : "AI Negotiation Engine"}
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            {sellerEmpty
              ? "Counter strategy, minimum acceptable price, listing tweaks, and a polished response to the buyer’s agent — all generated from this listing."
              : "Three offer tiers, broker questions, motivation clues, and a realtor email — generated from this deal’s actual numbers."}
          </p>
          <Button
            onClick={onRun}
            data-testid="neg-tab-run"
            className={`mt-6 text-white transition-all hover:-translate-y-0.5 hover:shadow-md ${
              sellerEmpty ? "bg-amber-600 hover:bg-amber-700" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {sellerEmpty ? "Run Listing Strategy" : "Run Negotiation Analysis"}
          </Button>
        </div>
      </div>
    );
  }

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(neg.realtor_email_template || "");
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please select and copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="neg-tab-content">
      <MAOTargetCard deal={deal} neg={neg} />

      {/* Phase 36 Batch AB — Document signals card / upload nudge */}
      <DocumentSignalsSection
        neg={neg}
        deal={deal}
        onSwitchTab={onSwitchTab}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Offer tiers</h3>
          <p className="text-xs text-slate-500">Asking: {fmt.money(neg.asking_price)}</p>
        </div>
        <Button
          variant="outline"
          onClick={onRun}
          disabled={running}
          data-testid="neg-tab-regenerate"
          className="border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <OfferCard tierKey="conservative_offer" tier={neg.offers.conservative_offer} asking={neg.asking_price} />
        <OfferCard tierKey="recommended_offer" tier={neg.offers.recommended_offer} asking={neg.asking_price} />
        <OfferCard tierKey="max_offer" tier={neg.offers.max_offer} asking={neg.asking_price} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="neg-broker-questions">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <MessagesSquare className="h-4 w-4" /> Broker questions
          </h3>
          <ol className="mt-4 space-y-2 text-sm text-slate-700">
            {(neg.broker_questions || []).map((q, i) => (
              <li key={`bq-${i}-${q.slice(0, 24)}`} className="flex gap-2">
                <span className="font-semibold text-indigo-600">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="neg-motivation-clues">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Search className="h-4 w-4" /> Seller motivation clues
          </h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            {(neg.seller_motivation_clues || []).map((m, i) => (
              <li key={`smc-${i}-${m.slice(0, 24)}`} className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-slate-400" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="neg-email-template">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Realtor email template</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={copyEmail}
            data-testid="neg-copy-email"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {copied ? <Check className="mr-2 h-4 w-4 text-emerald-600" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copied" : "Copy to clipboard"}
          </Button>
        </div>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-5 font-mono text-xs leading-relaxed text-slate-800">
{neg.realtor_email_template}
        </pre>
      </section>

      {neg.ai_status === "fallback" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          AI service unavailable — showing baseline analysis.
        </div>
      )}
    </div>
  );
}


// Phase 36 Batch AA.4 — 70 % MAO auto-prefill (companion to the
// Step-6 "winner" nudge). Shows the investor's own Maximum Allowable
// Offer alongside the AI's offer tiers, so a flipper/BRRRR operator
// can sanity-check that the AI's "Recommended" tier isn't drifting
// above their personal walk-away ceiling.
//
//   MAO = ARV × 0.70 − rehab budget
//
// Visibility:
//   - flip / brrrr only
//   - ARV and either repair_budget OR rehab_estimate must be present
//     (deal might be early-stage with no repair number yet — in that
//     case we show the formula and a friendly "fill in rehab budget"
//     hint instead of pretending MAO is just ARV × 0.70).
//
// Tracking: posthog `mao_prefilled` once per mount when the field
// auto-populates. User overrides are NOT tracked separately for v1.
function MAOTargetCard({ deal, neg }) {
  const fmt = useFmt();
  const dealType = (deal?.deal_type || "").toLowerCase();
  const arv = Number(deal?.arv) || 0;
  const repair = Number(deal?.repair_budget) || Number(deal?.rehab_estimate) || 0;

  const isFlipOrBrrrr = dealType === "flip" || dealType === "brrrr";
  const mao = useMemo(() => {
    if (!isFlipOrBrrrr || arv <= 0) return null;
    return arv * 0.7 - repair;
  }, [isFlipOrBrrrr, arv, repair]);

  // `null` override = no user edit yet → derive from MAO.
  // Anything else (incl. "") = user has touched → use their value verbatim.
  // This pattern lets `mao` reactively flow into the input WITHOUT a
  // setState-in-effect (which the strict ESLint rule rejects) while
  // still guaranteeing user overrides are never clobbered.
  const [override, setOverride] = useState(null);
  const derivedDefault =
    mao != null && mao > 0 ? String(Math.round(mao)) : "";
  const target = override == null ? derivedDefault : override;

  // posthog tracking — fires once per mount when prefill applies.
  useEffect(() => {
    if (mao == null || mao <= 0) return;
    try {
      window.posthog?.capture?.("mao_prefilled", {
        deal_type: dealType,
        arv,
        repair_budget: repair,
        mao: Math.round(mao),
        deal_id: deal?.id,
      });
    } catch {
      /* DNT / blocked — ignore */
    }
    // Intentionally fires once per (deal × mount): the analytics event
    // is "this user saw a prefilled MAO on this deal", not "this user
    // changed their inputs", which would be a different signal.
  }, [deal?.id, mao, dealType, arv, repair]);

  if (!isFlipOrBrrrr) return null;
  if (arv <= 0) return null;

  const recommendedPrice = neg?.offers?.recommended_offer?.price;
  const targetNum = Number(target);
  const targetValid = Number.isFinite(targetNum) && targetNum > 0;
  const deltaVsRecommended =
    targetValid && recommendedPrice ? targetNum - recommendedPrice : null;
  const repairMissing = repair <= 0;

  return (
    <section
      data-testid="mao-target-card"
      data-mao={mao != null ? Math.round(mao) : ""}
      className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-white p-5 shadow-sm sm:p-6"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
          <Calculator className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
              Your MAO target
            </h3>
            <span className="text-xs font-medium text-slate-500">
              70 % MAO rule · ARV × 0.70 − rehab
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Auto-prefilled from this {dealType === "flip" ? "flip" : "BRRRR"}’s
            ARV of {fmt.money(arv)}
            {repair > 0 ? <> and a rehab budget of {fmt.money(repair)}</> : null}.
            {" "}You can override it — it’s just a starting line.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-1 sm:col-span-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Target offer
              </span>
              <Input
                data-testid="mao-target-input"
                type="number"
                inputMode="numeric"
                value={target}
                onChange={(e) => setOverride(e.target.value)}
                placeholder={mao != null ? String(Math.round(mao)) : "0"}
                className="text-lg font-semibold tracking-tight tabular-nums"
              />
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                70 % MAO baseline
              </span>
              <div
                data-testid="mao-baseline-value"
                className="text-lg font-bold tabular-nums text-slate-900"
              >
                {mao != null && mao > 0 ? fmt.money(Math.round(mao)) : "—"}
              </div>
              {repairMissing && (
                <div className="text-[11px] text-amber-700">
                  Add a rehab budget on the deal to sharpen this.
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                vs AI recommended
              </span>
              {deltaVsRecommended != null ? (
                <div
                  data-testid="mao-delta-vs-recommended"
                  className={`text-lg font-bold tabular-nums ${
                    deltaVsRecommended <= 0
                      ? "text-emerald-700"
                      : "text-amber-700"
                  }`}
                >
                  {deltaVsRecommended <= 0 ? "−" : "+"}
                  {fmt.money(Math.abs(Math.round(deltaVsRecommended)))}
                </div>
              ) : (
                <div className="text-sm text-slate-500">
                  Run negotiation to compare
                </div>
              )}
              {deltaVsRecommended != null && deltaVsRecommended > 0 && (
                <div className="text-[11px] text-amber-700">
                  AI tier is above your MAO — push back.
                </div>
              )}
            </div>
          </div>

          {/* Phase 36 Batch AF — persist the counter-offer to the
              deal so the Financials / Scenarios / Projection tabs all
              show the same numbers. Only enabled once the user typed
              a real number AND it differs from the deal's current
              purchase_price (no point firing a no-op PATCH). */}
          <MAOTargetSaveRow
            deal={deal}
            target={target}
            targetValid={targetValid}
            targetNum={targetNum}
          />
        </div>
      </div>
    </section>
  );
}


function MAOTargetSaveRow({ deal, target, targetValid, targetNum }) {
  const [saving, setSaving] = useState(false);
  const [savedAmount, setSavedAmount] = useState(null);
  const currentPrice = Number(deal?.purchase_price) || 0;
  const basis = deal?.effective_purchase_price_basis || "purchase_price";
  const alreadyApplied =
    targetValid &&
    Math.round(targetNum) === Math.round(currentPrice) &&
    basis === "user_target_offer";

  const handleSave = async () => {
    if (!targetValid) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/deals/${deal.id}/target-offer`, {
        amount: targetNum,
        basis: "user_target_offer",
        note: "set from MAO Target card",
      });
      setSavedAmount(targetNum);
      try {
        window.posthog?.capture?.("counter_offer_persisted", {
          deal_id: deal.id,
          amount: targetNum,
          source: "mao_target_card",
          previous_basis: basis,
        });
      } catch {
        /* posthog blocked — ignore */
      }
      toast.success(
        `Counter-offer $${Math.round(targetNum).toLocaleString()} applied to financials`
      );
      // Soft signal: clients reading deal-level state should refetch.
      // We can't update the parent deal directly from here without
      // prop drilling, so we dispatch a window event the parent
      // listens to in DealDetail.
      try {
        window.dispatchEvent(
          new CustomEvent("runidea:deal-updated", { detail: data })
        );
      } catch {
        /* ignore */
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save.";
      toast.error(typeof msg === "string" ? msg : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-indigo-100/60 pt-4">
      <p className="text-xs text-slate-600">
        {alreadyApplied
          ? "Your counter-offer is already driving the Financials tab."
          : "Apply this counter-offer to the Financials, Scenarios, and Report tabs."}
      </p>
      <Button
        type="button"
        disabled={!targetValid || saving || alreadyApplied}
        onClick={handleSave}
        data-testid="mao-target-save"
        size="sm"
        className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-50"
      >
        {saving ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : alreadyApplied ? (
          <Check className="mr-1.5 h-3.5 w-3.5" />
        ) : null}
        {alreadyApplied
          ? "Applied"
          : savedAmount != null
          ? "Update counter-offer"
          : "Save & apply to analysis"}
      </Button>
    </div>
  );
}


// Phase 36 — Seller-side negotiation view. Renders the listing-pitch
// payload returned by run_seller_negotiation (no offers grid; instead
// minimum acceptable price, list-price recommendation, counter
// strategy, buyer motivation clues, response template, listing tweaks).
function SellerNegotiationView({ neg, deal, onRun, running }) {
  const fmt = useFmt();
  const [copied, setCopied] = useState(false);
  const recommendation = (neg?.list_price_recommendation || "HOLD").toUpperCase();
  const recTone =
    recommendation === "REDUCE"
      ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
      : recommendation === "RAISE"
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
        : "bg-amber-50 text-amber-800 ring-1 ring-amber-200";

  const copyResponse = async () => {
    try {
      await navigator.clipboard.writeText(neg?.response_template || "");
      setCopied(true);
      toast.success("Response copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please select and copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="neg-tab-content-seller">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Listing strategy
          </h3>
          <p className="text-xs text-slate-500">
            Asking: <span className="tabular-nums">{fmt.money(neg.asking_price)}</span>
            {neg.buyer_offer ? (
              <>
                {"  ·  "}Best buyer offer:{" "}
                <span className="tabular-nums">{fmt.money(neg.buyer_offer)}</span>
              </>
            ) : null}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={onRun}
          disabled={running}
          data-testid="neg-tab-regenerate-seller"
          className="border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 shadow-sm" data-testid="seller-recommendation-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">
            List-price recommendation
          </div>
          <div className="mt-1 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-700" />
            <span className={`rounded-md px-2 py-0.5 text-sm font-bold ${recTone}`}>
              {recommendation}
            </span>
          </div>
          <p className="mt-3 text-xs text-slate-700">
            {neg.list_price_recommendation_justification || ""}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="seller-floor-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Minimum acceptable price
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">
            {fmt.money(neg.minimum_acceptable_price)}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Below this number, walk away. Use it privately — never share with the buyer.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="seller-counter-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Counter strategy
          </div>
          <p className="mt-1 text-sm leading-relaxed text-slate-800">
            {neg.counter_strategy || "—"}
          </p>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-justification">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <HandCoins className="h-4 w-4" /> Why this strategy
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-700">
          {neg.justification || "—"}
        </p>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-buyer-clues">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Search className="h-4 w-4" /> Buyer motivation clues
          </h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            {(neg.buyer_motivation_clues || []).map((m, i) => (
              <li key={`bmc-${i}-${String(m).slice(0, 24)}`} className="flex gap-2">
                <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-amber-500" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-listing-tweaks">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Megaphone className="h-4 w-4" /> Listing improvement suggestions
          </h3>
          <ol className="mt-4 space-y-2 text-sm text-slate-700">
            {(neg.listing_tweak_suggestions || []).map((t, i) => (
              <li key={`tweak-${i}-${String(t).slice(0, 24)}`} className="flex gap-2">
                <span className="font-semibold text-amber-700">{i + 1}.</span>
                <span>{t}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-response-template">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <Mail className="h-4 w-4" /> Response to buyer’s agent
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={copyResponse}
            data-testid="seller-copy-response"
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {copied ? <Check className="mr-2 h-4 w-4 text-emerald-600" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copied" : "Copy to clipboard"}
          </Button>
        </div>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-5 font-mono text-xs leading-relaxed text-slate-800">
{neg.response_template || ""}
        </pre>
      </section>

      {neg.ai_status === "fallback" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          AI service unavailable — showing baseline strategy.
        </div>
      )}
    </div>
  );
}



// ─── Phase 36 Batch AB — Document Signals → Negotiation ─────────────────
// Two sibling components sharing the slot directly above the Offer Tiers
// grid:
//
//   1. DocumentSignalsCard — renders when the negotiation response
//      contains a non-empty `document_signals_used` list. Shows every
//      doc the AI actually weighted into the recommended offer, plus
//      the total adjustment %.
//   2. DocumentNudgeCard — renders ONLY when no docs are uploaded yet.
//      Dismissible per-session (sessionStorage flag scoped to deal_id)
//      so the user isn't nagged every time they reopen the tab.
//
// PostHog instrumentation:
//   - negotiation_used_doc_signals (fires once on first render with signals)
//   - negotiation_doc_nudge_shown / negotiation_doc_nudge_clicked / *_dismissed
//
// The "View documents →" / "Go to Documents →" links call
// onSwitchTab("documents") which the parent DealDetail wires to its
// controlled `<Tabs value={...} onValueChange={...} />`.
function DocumentSignalsSection({ neg, deal, onSwitchTab }) {
  // Skip sellers entirely — the seller view has its own layout and
  // doc-signals are buyer-side strategy by construction.
  const role = (neg?.role || deal?.role || "buyer").toLowerCase();
  if (role === "seller") return null;

  const signals = neg?.document_signals_used || [];
  if (signals.length > 0) {
    return (
      <DocumentSignalsCard
        neg={neg}
        deal={deal}
        onSwitchTab={onSwitchTab}
      />
    );
  }
  return (
    <DocumentNudgeCard
      deal={deal}
      onSwitchTab={onSwitchTab}
    />
  );
}


function _verdictTone(verdict) {
  switch ((verdict || "").toLowerCase()) {
    case "reject":
      return {
        cls: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
        icon: AlertTriangle,
        iconCls: "text-rose-600",
        label: "Reject",
      };
    case "concerns":
      return {
        cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
        icon: AlertTriangle,
        iconCls: "text-amber-600",
        label: "Concerns",
      };
    case "acceptable":
      return {
        cls: "bg-slate-100 text-slate-800 ring-1 ring-slate-200",
        icon: FileText,
        iconCls: "text-slate-500",
        label: "Acceptable",
      };
    case "strong":
      return {
        cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
        icon: ShieldCheck,
        iconCls: "text-emerald-600",
        label: "Strong",
      };
    default:
      return {
        cls: "bg-slate-100 text-slate-700",
        icon: FileText,
        iconCls: "text-slate-500",
        label: verdict || "n/a",
      };
  }
}


function DocumentSignalsCard({ neg, deal, onSwitchTab }) {
  const signals = neg?.document_signals_used || [];
  const totalPct = Number(neg?.document_adjustment_pct ?? 0);

  // Fire posthog once per (deal × signals-fingerprint) mount. The
  // fingerprint = doc_id list, so re-running negotiation on the same
  // doc set won't double-track.
  const fingerprint = signals.map((s) => s.doc_id || "").join("|");
  useEffect(() => {
    if (!signals.length) return;
    try {
      const ratings = signals
        .map((s) => Number(s.rating))
        .filter((v) => Number.isFinite(v));
      const avgRating = ratings.length
        ? ratings.reduce((a, b) => a + b, 0) / ratings.length
        : null;
      window.posthog?.capture?.("negotiation_used_doc_signals", {
        deal_id: deal?.id,
        doc_count: signals.length,
        avg_rating: avgRating != null ? Number(avgRating.toFixed(2)) : null,
        recommended_price_delta_pct: totalPct,
      });
    } catch {
      /* DNT / blocked — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, fingerprint]);

  const totalSign = totalPct > 0 ? "+" : "";
  const totalCls =
    totalPct < 0 ? "text-rose-700" : totalPct > 0 ? "text-emerald-700" : "text-slate-700";

  return (
    <section
      data-testid="neg-doc-signals-card"
      className="rounded-2xl border border-indigo-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
            <FileText className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
              AI used your uploaded documents
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {signals.length} document{signals.length === 1 ? "" : "s"} shaped this recommended offer.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSwitchTab?.("documents")}
          data-testid="neg-doc-signals-view-docs"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
        >
          View documents <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/40">
        {signals.map((s) => {
          const tone = _verdictTone(s.verdict);
          const Icon = tone.icon;
          const impact = Number(s.weighted_impact_pct ?? 0);
          const sign = impact > 0 ? "+" : "";
          const impactCls =
            impact < 0 ? "text-rose-700" : impact > 0 ? "text-emerald-700" : "text-slate-500";
          return (
            <li
              key={s.doc_id || `${s.category}-${s.filename}`}
              data-testid={`neg-doc-signal-${s.category}`}
              className="flex items-center gap-3 px-4 py-3"
            >
              <Icon className={`h-4 w-4 flex-none ${tone.iconCls}`} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-slate-900">
                    {s.category_label}
                  </span>
                  {Number.isFinite(s.rating) && (
                    <span className="text-xs text-slate-500">
                      {s.rating}/10
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone.cls}`}
                  >
                    {tone.label}
                  </span>
                  {s.critical && (
                    <span className="text-[10px] font-medium uppercase tracking-wider text-indigo-700">
                      critical
                    </span>
                  )}
                </div>
                {s.headline_red_flag && (
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {s.headline_red_flag}
                  </p>
                )}
              </div>
              <div className={`flex-none text-sm font-bold tabular-nums ${impactCls}`}>
                {sign}{impact.toFixed(1)}%
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-t border-slate-100 pt-3">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Net offer adjustment from documents
        </div>
        <div
          data-testid="neg-doc-adjustment-total"
          className={`text-lg font-bold tabular-nums ${totalCls}`}
        >
          {totalSign}{totalPct.toFixed(1)}%
        </div>
      </div>
    </section>
  );
}


function DocumentNudgeCard({ deal, onSwitchTab }) {
  const storageKey = `runidea:neg_doc_nudge_dismissed_${deal?.id || "_"}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Boolean(sessionStorage.getItem(storageKey));
    } catch {
      return false;
    }
  });

  // Fire posthog `negotiation_doc_nudge_shown` once per mount when the
  // nudge is actually painted.
  useEffect(() => {
    if (dismissed) return;
    try {
      window.posthog?.capture?.("negotiation_doc_nudge_shown", {
        deal_id: deal?.id,
        deal_type: (deal?.deal_type || "").toLowerCase(),
      });
    } catch {
      /* DNT / blocked — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, dismissed]);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      /* private browsing / quota — fall through */
    }
    setDismissed(true);
    try {
      window.posthog?.capture?.("negotiation_doc_nudge_dismissed", {
        deal_id: deal?.id,
      });
    } catch {
      /* ignore */
    }
  };

  const goToDocs = () => {
    try {
      window.posthog?.capture?.("negotiation_doc_nudge_clicked", {
        deal_id: deal?.id,
      });
    } catch {
      /* ignore */
    }
    onSwitchTab?.("documents");
  };

  return (
    <section
      data-testid="neg-doc-nudge"
      className="relative rounded-2xl border border-slate-200 bg-slate-50/70 p-5 shadow-sm sm:p-6"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="neg-doc-nudge-dismiss"
        className="absolute right-3 top-3 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-indigo-600 ring-1 ring-indigo-100">
          <FileText className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold tracking-tight text-slate-900">
            Sharpen this offer with your due-diligence docs
          </h3>
          <p className="mt-1 max-w-xl text-sm text-slate-600">
            Upload the inspection, HOA financials, or title docs and the AI
            will fold their findings into the recommended offer — citing the
            specific risks in your negotiation justification.
          </p>
          <button
            type="button"
            onClick={goToDocs}
            data-testid="neg-doc-nudge-cta"
            className="mt-3 inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
          >
            Go to Documents <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
